import { makeStyles } from '@material-ui/styles';
import { Tabs } from 'antd';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { BookingJourney } from 'common/constants/booking';
import BookingTableDetails from './booking-table-details';
import BookingStageAndPropertyNameFilters from './booking-stage-and-property-name-filters';

const useStyles = makeStyles(theme => ({
  wrapper: {
    minHeight: 300,

    '& .ant-tabs-bar': {
      margin: '0',
    },

    '& .ant-tabs-tab': {
      padding: '0 24px !important',
      marginRight: '4px !important',

      '&.ant-tabs-tab-active': {
        position: 'relative',
        color: `${theme.colors.titleColor} !important`,
        ...theme.typography.h5,
      },

      '&:hover': {
        color: `${theme.colors.titleColor} !important`,
      },
    },
  },
  tables: {
    padding: '16px 24px',
    borderRadius: `0 0 ${theme.borderRadius.small}px ${theme.borderRadius.small}px`,
    backgroundColor: theme.colors.gray1,
    border: `1px solid ${theme.colors.borderColor}`,
    borderTop: 'none',
  },
}));

const BookingTables = ({
  className,
  bookingType,
  setBookingType,
  bookingStage,
  setBookingStage,
  otherTableFilter,
  setOtherTableFilter,
  statisticsData,
  tableData,
  tableLoading,
  refetchTableData,
  pendingOrderCount,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isHerculesBooking = useMemo(
    () => bookingType === BookingJourney.SEMI_AUTOMATIC,
    [bookingType],
  );

  const isInstantBooking = useMemo(
    () => bookingType === BookingJourney.INSTANT_BOOK,
    [bookingType],
  );

  const enableTabLength = useMemo(() => {
    return [
      statisticsData?.company?.herculesAllOrders?.totalCount,
      statisticsData?.company?.manualAllBookings?.totalCount,
      statisticsData?.company?.instantAllOrders?.totalCount,
    ].filter(item => !!item).length;
  }, [
    statisticsData?.company?.herculesAllOrders?.totalCount,
    statisticsData?.company?.manualAllBookings?.totalCount,
    statisticsData?.company?.instantAllOrders?.totalCount,
  ]);

  const handleClickTab = type => {
    setBookingType(type);
  };

  return (
    <div className={classNames(classes.wrapper, className)}>
      <If condition={enableTabLength > 1}>
        <Tabs onChange={handleClickTab} type="card" activeKey={bookingType}>
          <If condition={statisticsData?.company?.herculesAllOrders?.totalCount > 0}>
            <Tabs.TabPane
              tab={t('landlord.long_tail.booking.list.hercules_booking_title')}
              key={BookingJourney.SEMI_AUTOMATIC}
            />
          </If>

          <If condition={statisticsData?.company?.instantAllOrders?.totalCount > 0}>
            <Tabs.TabPane
              tab={t('landlord.long_tail.booking.list.instant_bookings')}
              key={BookingJourney.INSTANT_BOOK}
            />
          </If>

          <If condition={statisticsData?.company?.manualAllBookings?.totalCount > 0}>
            <Tabs.TabPane
              tab={t('landlord.long_tail.booking.list.manual_booking_title')}
              key={BookingJourney.MANUAL}
            />
          </If>
        </Tabs>
      </If>
      <div className={classes.tables}>
        <BookingStageAndPropertyNameFilters
          setBookingStage={setBookingStage}
          bookingStage={bookingStage}
          bookingType={bookingType}
          pendingConfirmationOrderCount={pendingOrderCount.confirmation}
          pendingRefundOrderCount={pendingOrderCount.refund}
        />
        <BookingTableDetails
          tableData={tableData}
          loading={tableLoading}
          isHerculesBooking={isHerculesBooking}
          isInstantBooking={isInstantBooking}
          otherTableFilter={otherTableFilter}
          setOtherTableFilter={setOtherTableFilter}
          refetchData={refetchTableData}
        />
      </div>
    </div>
  );
};

BookingTables.propTypes = {
  className: PropTypes.string,
  bookingType: PropTypes.string,
  setBookingType: PropTypes.func,
  bookingStage: PropTypes.string,
  setBookingStage: PropTypes.func,
  otherTableFilter: PropTypes.object,
  setOtherTableFilter: PropTypes.func,
  tableData: PropTypes.object,
  tableLoading: PropTypes.bool,
  refetchTableData: PropTypes.func,
  pendingOrderCount: PropTypes.object,
  statisticsData: PropTypes.object,
};

export default BookingTables;
